import { ErrorHandler, Injectable, inject } from '@angular/core';
import { NotificationService } from './notification.service';
import { NotificationTypeEnum } from '../enums/notificationType.enum';
import { ErrorLocationEnum } from '../enums/errorLocation.enum';

@Injectable()
export class CustomErrorHandler implements ErrorHandler {
  private notificationService = inject(NotificationService);

  /**
   * Handles and reports application errors in a unified way.
   *
   * Behavior:
   * - Ignores errors whose name is 'EmptyError' (commonly emitted by RxJS when no values are produced).
   * - Distinguishes between server-originated and client-originated errors using the `cause` property
   *   (expected to match a value from `ErrorLocationEnum`).
   * - Logs the raw error object and a formatted message to the console.
   * - Displays a user-facing notification with line breaks normalized (CRLF -> <br/>).
   *
   * Notification:
   * - Type: Error (uses NotificationTypeEnum.Error)
   * - Duration: 2000 ms
   * - Closes automatically; is dismissible (true)
   *
   * @param error The error object to process. Expected shape:
   *  {
   *    name?: string;
   *    message: string;
   *    cause?: ErrorLocationEnum; // When equal to ErrorLocationEnum.Server, treated as server-side
   *  }
   *
   * @remarks
   * If `error.cause` is not `ErrorLocationEnum.Server`, the error is treated as a client-side issue.
   * Newlines in the message are converted to HTML <br/> tags prior to notification display.
   *
   * @example
   * try {
   *   await apiCall();
   * } catch (err) {
   *   this.handleError(err);
   * }
   *
   * @example
   * // Server-side shaped error
   * this.handleError({
   *   name: 'HttpErrorResponse',
   *   message: '500 Internal Server Error',
   *   cause: ErrorLocationEnum.Server
   * });
   *
   * @example
   * // Client-side error
   * this.handleError(new Error('Unexpected null reference'));
   *
   * @internal
   * Ensure upstream code does not pass sensitive data into `error.message`, as it is user-visible.
   */

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  handleError(error: any): void {
    if (error.name !== 'EmptyError') {
      console.error(error);
      let errorMessage = '';
      if (error.cause === ErrorLocationEnum.Server) {
        // Server-side error
        errorMessage = error.message;
      } else {
        // Client-side error
        errorMessage = `Client error: ${error.message}`;
      }
      console.error(errorMessage);
      this.notificationService.show(
        NotificationTypeEnum.Error,
        errorMessage.replaceAll('\r\n', '<br/>'),
        2000,
        true,
      );
    }
  }
}
